<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { formatPhoneNumber } from '@/shared/lib'
import CButton from './CButton.vue'
import CDialog from './CDialog.vue'
import CPhoneNumberDialog from './CPhoneNumberDialog.vue'

interface Account {
  id: number | string
  name: string
}

interface Props {
  modelValue?: boolean
  accounts: Account[]
  currentAccountId?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  accounts: () => [],
  currentAccountId: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'select': [account: Account]
  'addAccount': [phoneNumber: string]
  'verifyCode': [code: string, phoneNumber: string]
  'deleteAccount': [account: Account]
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

const selectedAccountId = ref<string>('')
const showPhoneDialog = ref(false)
const isDeleteDialogOpen = ref(false)
const accountToDelete = ref<Account | null>(null)

// Track if we've initialized for this dialog open session
const hasInitialized = ref(false)

// Helper function to check if an account is selected
function isAccountSelected(accountId: number | string): boolean {
  return selectedAccountId.value === String(accountId)
}

// Initialize selected account when dialog opens
watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) {
      // Dialog is opening - initialize selection
      if (props.currentAccountId && props.currentAccountId.trim() !== '') {
        selectedAccountId.value = String(props.currentAccountId)
      }
      else if (props.accounts.length > 0) {
        selectedAccountId.value = String(props.accounts[0].id)
      }
      else {
        selectedAccountId.value = ''
      }
      hasInitialized.value = true
    }
    else {
      // Dialog is closing - reset
      hasInitialized.value = false
      selectedAccountId.value = ''
      isDeleteDialogOpen.value = false
      accountToDelete.value = null
    }
  },
  { immediate: true },
)

// Helper function to check if a string is a phone number
function isPhoneNumber(str: string): boolean {
  if (!str) 
    return false
  const cleaned = str.replace(/\D/g, '')
  return cleaned.length >= 10 && cleaned.length <= 15
}

// Format account display name
function formatAccountDisplay(name: string): string {
  if (!name) 
    return name
  if (isPhoneNumber(name)) {
    return formatPhoneNumber(name)
  }
  return name
}

function selectAccount(account: Account) {
  selectedAccountId.value = String(account.id)
}

function confirmSelection() {
  const account = props.accounts.find(
    acc => acc.id.toString() === selectedAccountId.value,
  )
  if (account) {
    emit('select', account)
    closeDialog()
  }
}

function openPhoneDialog() {
  showPhoneDialog.value = true
}

function handlePhoneDialogClose(value: boolean) {
  if (!value) {
    showPhoneDialog.value = false
    // Don't close main dialog - let user continue selecting
  }
}

function handleGetCode(phoneNumber: string) {
  emit('addAccount', phoneNumber)
}

function handleVerifyCode(code: string, phoneNumber: string) {
  emit('verifyCode', code, phoneNumber)
  showPhoneDialog.value = false
  closeDialog()
}

function handleResendCode() {
  // Resend is handled internally by CPhoneNumberDialog
}

function closeDialog() {
  isOpen.value = false
  selectedAccountId.value = ''
  isDeleteDialogOpen.value = false
  accountToDelete.value = null
}

function openDeleteDialog(account: Account) {
  accountToDelete.value = account
  isDeleteDialogOpen.value = true
}

function closeDeleteDialog() {
  isDeleteDialogOpen.value = false
  accountToDelete.value = null
}

function confirmDeleteAccount() {
  if (!accountToDelete.value) 
    return
  emit('deleteAccount', accountToDelete.value)
  closeDeleteDialog()
}
</script>

<template>
  <div>
    <!-- Account Select Dialog -->
    <CDialog
      v-if="!showPhoneDialog"
      v-model="isOpen"
      class="max-w-[480px] !w-[90%] sm:!w-[480px]"
      :title="$t('select_account')"
    >
      <template #header>
        <div class="flex items-center justify-between w-full">
          <h3 class="text-lg font-semibold text-text-regular">
            {{ $t("select_account") }}
          </h3>
          <button
            class="text-text-secondary hover:text-text-regular transition-colors"
            @click="closeDialog"
          >
            <i class="icon-close text-xl" />
          </button>
        </div>
      </template>

      <div class="flex flex-col gap-3">
        <!-- Account list -->
        <div v-if="accounts.length > 0" class="flex flex-col gap-2">
          <p class="text-sm text-text-secondary mb-1">
            {{ $t("choose_account_or_add_new") }}
          </p>
          <div
            v-for="account in accounts"
            :key="account.id"
            class="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all"
            :class="
              isAccountSelected(account.id)
                ? 'border-brand-primary bg-primary-light-9'
                : 'border-border-light-3 hover:border-brand-primary'
            "
            @click="selectAccount(account)"
          >
            <div class="flex items-center gap-3 flex-1">
              <div
                class="w-5 h-5 rounded-full border-2 flex items-center justify-center"
                :class="
                  isAccountSelected(account.id)
                    ? 'border-brand-primary'
                    : 'border-border-light-3'
                "
              >
                <div
                  v-if="isAccountSelected(account.id)"
                  class="w-2.5 h-2.5 rounded-full bg-brand-primary"
                />
              </div>
              <div class="flex items-center gap-2">
                <i class="icon-store text-lg text-text-secondary" />
                <span class="text-sm text-text-regular">{{
                  formatAccountDisplay(account.name)
                }}</span>
              </div>
            </div>
            <button
              type="button"
              class="flex items-center gap-1 px-2 py-1 rounded-md text-red-500 hover:bg-red-50 transition-colors"
              :aria-label="$t('delete')"
              @click.stop="openDeleteDialog(account)"
            >
              <CIcon icon="hugeicons:delete-02" class="text-base" />
              <span class="text-xs font-medium">{{ $t("delete") }}</span>
            </button>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else class="text-center py-6 text-text-secondary text-sm">
          {{ $t("no_accounts_available") }}
        </div>

        <!-- Divider -->
        <div class="flex items-center gap-3 my-2">
          <div class="flex-1 h-px bg-border-light" />
          <span class="text-xs text-text-secondary">{{ $t("or") }}</span>
          <div class="flex-1 h-px bg-border-light" />
        </div>

        <!-- Add new account button -->
        <button
          class="flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-brand-primary text-brand-primary hover:bg-primary-light-9 transition-colors"
          @click="openPhoneDialog"
        >
          <i class="icon-add text-lg" />
          <span class="text-sm font-medium">{{ $t("add_new_account") }}</span>
        </button>
      </div>

      <template #footer>
        <div class="flex justify-end gap-3">
          <CButton
            variant="ghost"
            class="!bg-white !border-border-light !text-text-secondary"
            @click="closeDialog"
          >
            {{ $t("cancel") }}
          </CButton>
          <CButton
            type="primary"
            :disabled="!selectedAccountId"
            class="text-sm disabled:opacity-50 transition-300"
            @click="confirmSelection"
          >
            {{ $t("confirm") }}
          </CButton>
        </div>
      </template>
    </CDialog>

    <!-- Delete Confirmation Dialog -->
    <CDialog
      v-model="isDeleteDialogOpen"
      :title="$t('delete_account')"
      class="max-w-[400px] !w-[90%]"
    >
      <p class="text-sm text-text-regular">
        {{
          $t("delete_account_confirm", {
            account: formatAccountDisplay(accountToDelete?.name || ""),
          })
        }}
      </p>
      <template #footer>
        <div class="flex items-center justify-end gap-3">
          <CButton
            variant="ghost"
            class="!bg-white !border-border-light !text-text-secondary"
            @click="closeDeleteDialog"
          >
            {{ $t("cancel") }}
          </CButton>
          <CButton type="primary" @click="confirmDeleteAccount">
            {{ $t("delete") }}
          </CButton>
        </div>
      </template>
    </CDialog>

    <!-- Phone Number Dialog for adding new account -->
    <CPhoneNumberDialog
      v-model="showPhoneDialog"
      @get-code="handleGetCode"
      @verify-code="handleVerifyCode"
      @resend-code="handleResendCode"
      @update:model-value="handlePhoneDialogClose"
    />
  </div>
</template>
